import { useRef, useState } from 'react';
import type { WaiverType } from '../../shared/api';
import SignaturePad, { type SignaturePadHandle } from '../../components/SignaturePad';
import { Banner, Button, DataTable, Field, Input, Modal, Pill, Select, Td } from '../../components/ui';
import { api, ApiError, fileUrl } from '../../lib/api';
import { fmtDate, fmtDateTime, money, WAIVER_LABELS } from '../../lib/format';
import { useSession } from '../../lib/session';
import type { TabProps } from './types';

const TYPES = Object.keys(WAIVER_LABELS) as WaiverType[];

/**
 * Managers and admins execute a waiver outright. Anyone below that files an
 * approval request carrying the identical payload, which the office reviews on
 * /approvals — the same form either way, only the verb changes.
 */
export default function WaiversTab({ job, reload }: TabProps) {
  const { can, contractor } = useSession();
  const mayExecute = can('manager');
  // Emailing a waiver is LienGuardian sending it in the company's name, which
  // needs a verified person. Signing it to download and send yourself does not.
  const verified = !!contractor?.personVerifiedAt;
  const sigRef = useRef<SignaturePadHandle>(null);

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    waiverType: 'conditional_partial' as WaiverType,
    amount: '',
    throughDate: '',
    paymentReference: '',
    signerName: '',
    sendToEmail: '',
  });
  const [delivery, setDelivery] = useState<'self' | 'email'>(verified ? 'email' : 'self');

  const lock = job.waiverLocks?.[draft.waiverType];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const signatureImage = sigRef.current?.toDataUrl();
    if (mayExecute && !signatureImage) return setError('A signature is required to execute a waiver.');
    setBusy(true);
    const payload = {
      waiverType: draft.waiverType,
      amount: Number(draft.amount),
      throughDate: draft.throughDate || null,
      paymentReference: draft.paymentReference || null,
      signerName: draft.signerName,
      sendToEmail: delivery === 'email' ? draft.sendToEmail : null,
      // A request still needs a signature the approver can rely on; when the
      // requester has not drawn one, the approver signs at review time.
      signatureImage: signatureImage ?? 'requested-pending-signature',
    };
    try {
      if (mayExecute) {
        await api.post(`/api/jobs/${job.id}/waivers`, payload);
        setDone(
          delivery === 'email'
            ? 'Waiver signed and emailed.'
            : 'Waiver signed. Download it from the list below and send it yourself.',
        );
      } else {
        await api.post(`/api/jobs/${job.id}/approval-requests`, { actionType: 'waiver_sign', payload });
        setDone('Request sent to the office for approval.');
      }
      setOpen(false);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit the waiver');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted">
          {job.unpaidSuppliers > 0
            ? `${job.unpaidSuppliers} unpaid supplier obligation${job.unpaidSuppliers === 1 ? '' : 's'} may lock unconditional waivers.`
            : 'No supplier obligations are blocking a waiver.'}
        </p>
        <Button onClick={() => setOpen(true)}>{mayExecute ? 'Execute waiver' : 'Request waiver'}</Button>
      </div>

      {done && <Banner tone="ok">{done}</Banner>}
      {error && !open && <Banner tone="urgent">{error}</Banner>}

      <DataTable head={['Type', 'Amount', 'Through', 'Signed', 'Sent to', '']} empty="No waivers on this job yet.">
        {job.waivers.map((w) => {
          const href = fileUrl(w.documentUrl);
          return (
            <tr key={w.id} className="border-b border-border/60">
              <Td className="font-medium">{WAIVER_LABELS[w.waiverType]}</Td>
              <Td>{money(w.amount)}</Td>
              <Td className="text-muted">{fmtDate(w.effectiveAt)}</Td>
              <Td className="text-muted">{fmtDateTime(w.signedAt)}</Td>
              <Td className="text-muted">{w.sentToEmail ?? 'You (download)'}</Td>
              <Td>
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer" className="text-sm font-semibold text-tint hover:underline">
                    Download
                  </a>
                ) : (
                  <Pill>No file</Pill>
                )}
              </Td>
            </tr>
          );
        })}
      </DataTable>

      {open && (
        <Modal title={mayExecute ? 'Execute lien waiver' : 'Request lien waiver'} onClose={() => setOpen(false)} wide>
          <form onSubmit={submit} className="space-y-4">
            {!mayExecute && (
              <Banner tone="watch" title="This goes to the office for approval">
                Your seat cannot sign a waiver. A manager reviews this request and signs it — nothing is sent until they do.
              </Banner>
            )}

            <Field label="Waiver type">
              <Select value={draft.waiverType} onChange={(e) => setDraft({ ...draft, waiverType: e.target.value as WaiverType })}>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {WAIVER_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>

            {lock?.locked && <Banner tone="urgent" title="This waiver type is locked">{lock.reason}</Banner>}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Amount">
                <Input required type="number" min="0.01" step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
              </Field>
              <Field label="Through date (partial waivers)">
                <Input type="date" value={draft.throughDate} onChange={(e) => setDraft({ ...draft, throughDate: e.target.value })} />
              </Field>
            </div>

            <Field label="Payment reference" hint="Check number or wire reference, printed on the waiver.">
              <Input value={draft.paymentReference} onChange={(e) => setDraft({ ...draft, paymentReference: e.target.value })} />
            </Field>

            <Field label="Signer name">
              <Input required value={draft.signerName} onChange={(e) => setDraft({ ...draft, signerName: e.target.value })} />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="How it's delivered">
                <Select value={delivery} onChange={(e) => setDelivery(e.target.value as 'self' | 'email')}>
                  <option value="self">Download and send it myself</option>
                  <option value="email" disabled={!verified}>
                    Email it through LienGuardian{verified ? '' : ' (verify who you are first)'}
                  </option>
                </Select>
              </Field>
              {delivery === 'email' && (
                <Field label="Send to email">
                  <Input required type="email" value={draft.sendToEmail} onChange={(e) => setDraft({ ...draft, sendToEmail: e.target.value })} />
                </Field>
              )}
            </div>

            {mayExecute && <SignaturePad ref={sigRef} label="Signature" />}
            {error && <Banner tone="urgent">{error}</Banner>}

            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !!lock?.locked}>
                {busy ? 'Submitting…' : !mayExecute ? 'Send request' : delivery === 'email' ? 'Sign and email waiver' : 'Sign waiver'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
